/**
 * Модуль для работы с датами и их форматированием на основе библиотеки dayjs.
 * Поддерживает русскую и казахскую локализации.
 * Предоставляет функции для получения форматированной даты с различными опциями.
 *
 * @see https://day.js.org/en/
 * @module dateUtils
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ru';
import 'dayjs/locale/kk';

import { LanguageCodeEnum } from '@/shared/config/common/constants/lang';

import { capitalizeFirstLetter } from '../string/capitalizeFirstLetter';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

interface FormattedOptions {
  format: string;
  locale?: LanguageCodeEnum;
  withDay?: boolean;
  capitalizeFirst?: boolean;
}

/**
 * Возвращает день недели по переданной строке даты и локали.
 *
 * @param {string} dateString - Строка, представляющая дату.
 * @param {LanguageCodeEnum} [locale=LanguageCodeEnum.RU] - Локаль для форматирования дня недели. По умолчанию используется русский язык (RU).
 * @returns {string} - День недели на выбранном языке.
 */
function getDayByDate(dateString: string, locale: LanguageCodeEnum = LanguageCodeEnum.RU): string {
  return dayjs(dateString).locale(locale).format('dddd');
}

/**
 * Возвращает форматированную дату с учетом переданных опций.
 *
 * @param {string} dateString - Строка, представляющая дату.
 * @param {FormattedOptions} options - Объект опций для форматирования.
 * @returns {string} - Форматированная дата в указанном формате и локали.
 */
export function getFormattedDate(dateString: string, options: FormattedOptions): string {
  const { locale = LanguageCodeEnum.RU, format, capitalizeFirst = false, withDay = false } = options;

  const localizedDate = dayjs.utc(dateString).locale(locale === LanguageCodeEnum.RU ? 'ru' : 'kk');

  let result = localizedDate.format(format);

  if (withDay) {
    const dayOfWeek = getDayByDate(dateString, locale);
    result += `, ${dayOfWeek}`;
  }

  if (capitalizeFirst) {
    result = capitalizeFirstLetter(result);
  }

  return result;
}
